import { Classification, CustomClassification } from '@/services/types/Lead.types';

export type ClassificationOption = {
  value: string;
  label: string;
  className: string;
  isCustom: boolean;
};

const BUILTIN_OPTIONS: ClassificationOption[] = [
  {
    value: Classification.POSITIVE,
    label: 'conversations.positive',
    className: 'text-[#16A34A] bg-[#F0FDF4]',
    isCustom: false,
  },
  {
    value: Classification.ENGAGED,
    label: 'conversations.engaged',
    className: 'text-[#3B82F6] bg-[#EFF6FF]',
    isCustom: false,
  },
  {
    value: Classification.UNDETERMINED,
    label: 'conversations.noAnswer',
    className: 'text-[#F97316] bg-[#FFF7ED]',
    isCustom: false,
  },
  {
    value: Classification.NEGATIVE,
    label: 'conversations.negative',
    className: 'text-[#EF4444] bg-[#FEF2F2]',
    isCustom: false,
  },
];

export function buildClassificationOptions(
  customClassifications: CustomClassification[],
  t: (key: string) => string,
  opts?: { includeAll?: boolean }
): ClassificationOption[] {
  const options: ClassificationOption[] = BUILTIN_OPTIONS.map((o) => ({
    ...o,
    label: t(o.label),
  }));

  for (const cc of customClassifications) {
    options.push({
      value: cc.id,
      label: cc.label,
      className: `text-[${cc.text_color}] bg-[${cc.color}]`,
      isCustom: true,
    });
  }

  if (opts?.includeAll) {
    options.push({
      value: Classification.ALL,
      label: t('conversations.all'),
      className: 'text-[#6B7280] bg-[#F9FAFB]',
      isCustom: false,
    });
  }

  return options;
}

export function getClassificationLabel(
  value: string,
  customClassifications: CustomClassification[],
  t: (key: string) => string
): string {
  const builtinMap: Record<string, string> = {
    [Classification.POSITIVE]: t('conversations.positive'),
    [Classification.ENGAGED]: t('conversations.engaged'),
    [Classification.UNDETERMINED]: t('conversations.noAnswer'),
    [Classification.NEGATIVE]: t('conversations.negative'),
    [Classification.ALL]: t('conversations.all'),
  };
  if (builtinMap[value]) return builtinMap[value];
  const custom = customClassifications.find((c) => c.id === value);
  return custom?.label ?? value;
}

export function getClassificationStyle(
  value: string,
  customClassifications: CustomClassification[]
): { color: string; bg: string } {
  // CSS variables (globals.css) so the built-in statuses follow the theme.
  const builtinStyles: Record<string, { color: string; bg: string }> = {
    [Classification.POSITIVE]: { color: 'var(--status-positive)', bg: 'var(--status-positive-bg)' },
    [Classification.ENGAGED]: { color: 'var(--status-engaged)', bg: 'var(--status-engaged-bg)' },
    [Classification.UNDETERMINED]: {
      color: 'var(--status-undetermined)',
      bg: 'var(--status-undetermined-bg)',
    },
    [Classification.NEGATIVE]: { color: 'var(--status-negative)', bg: 'var(--status-negative-bg)' },
    [Classification.ALL]: { color: 'var(--status-neutral)', bg: 'var(--status-neutral-bg)' },
    [Classification.WAITING_AI_RESPONSE]: {
      color: 'var(--status-waiting)',
      bg: 'var(--status-waiting-bg)',
    },
  };
  if (builtinStyles[value]) return builtinStyles[value];
  const custom = customClassifications.find((c) => c.id === value);
  if (custom) return { color: custom.text_color, bg: custom.color };
  return { color: '#6B7280', bg: '#F9FAFB' };
}
